import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.ts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Plus, Trash2, ArrowLeft, ArrowRight, Save, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

interface TicketTier {
  name: string;
  price: number;
  description: string;
  capacity: number;
  perks: string[];
}

export default function CreateEvent() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    artist: '',
    venue: '',
    date: '',
    end_date: '',
    category: 'concert',
    is_virtual: false,
    price: 0,
    capacity: 100,
    tags: '',
    poster_url: '',
    currency: 'USD',
  });
  const [posterPreview, setPosterPreview] = useState<string | null>(null);
  const [ticketTiers, setTicketTiers] = useState<TicketTier[]>([
    { name: 'General Admission', price: 25, description: 'Standard entry', capacity: 100, perks: [] }
  ]);

  const categories = ['concert', 'festival', 'club', 'conference', 'workshop', 'other'];
  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
  ];

  const handlePosterUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPosterPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    try {
      setIsUploading(true);
      const result = await api.uploadPoster(file);
      setFormData({ ...formData, poster_url: result.url });
      toast.success('Poster uploaded successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload poster');
      setPosterPreview(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 1 && (!formData.title || !formData.artist || !formData.venue)) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (currentStep === 2 && (!formData.date)) {
      toast.error('Please select event date');
      return;
    }
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSave = async (status: 'draft' | 'published') => {
    try {
      setIsSaving(true);
      const tags = formData.tags.split(',').map(t => t.trim()).filter(Boolean);
      
      const eventData = {
        title: formData.title,
        description: formData.description,
        artist: formData.artist,
        venue: formData.venue,
        location: formData.venue, // Use venue as location
        date: formData.date ? new Date(formData.date).toISOString() : new Date().toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : undefined,
        category: formData.category,
        is_virtual: formData.is_virtual,
        currency: formData.currency,
        tags,
        poster_url: formData.poster_url,
        ticket_tiers: ticketTiers.map((tier, index) => ({
          type_id: `tier_${index}_${Date.now()}`,
          name: tier.name,
          price: tier.price,
          description: tier.description,
          perks: tier.perks || [],
          capacity: tier.capacity
        })),
      };

      const event = await api.createEvent(eventData);
      
      // If publishing, update the status
      if (status === 'published' && event.event_id) {
        await api.updateEvent(event.event_id, { status: 'published' });
      }
      
      toast.success(`Event ${status === 'draft' ? 'saved as draft' : 'published'} successfully!`);
      navigate('/manage-events');
    } catch (error: any) {
      console.error('Create event error:', error);
      toast.error(error.message || 'Failed to create event');
    } finally {
      setIsSaving(false);
    }
  };

  const addTicketTier = () => {
    setTicketTiers([...ticketTiers, {
      name: '',
      price: 0,
      description: '',
      capacity: 50,
      perks: []
    }]);
  };

  const removeTicketTier = (index: number) => {
    setTicketTiers(ticketTiers.filter((_, i) => i !== index));
  };

  const updateTicketTier = (index: number, field: string, value: any) => {
    const updated = [...ticketTiers];
    updated[index] = { ...updated[index], [field]: value };
    setTicketTiers(updated);
  };

  return (
    <div className="space-y-8 pb-20 md:pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold tracking-tight">
            Create Event
          </h1>
          <p className="text-sm md:text-lg text-gray-400 mt-1 md:mt-2">
            Step {currentStep} of 4
          </p>
        </div>
        <Button variant="ghost" onClick={() => navigate('/manage-events')} className="self-start">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Cancel
        </Button>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-2 md:gap-4">
        {[1, 2, 3, 4].map((step) => (
          <div key={step} className="flex items-center flex-1">
            <div
              className={`h-1.5 md:h-2 flex-1 rounded-full transition-colors ${
                step <= currentStep ? 'bg-primary' : 'bg-white/10'
              }`}
            />
          </div>
        ))}
      </div>

      {/* Step 1: Basic Info */}
      {currentStep === 1 && (
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Event Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Summer Music Festival 2025"
                className="glass border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe your event..."
                className="glass border-white/10 min-h-[120px]"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="artist">Artist/Performer *</Label>
                <Input
                  id="artist"
                  value={formData.artist}
                  onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                  placeholder="DJ XYZ"
                  className="glass border-white/10"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full h-10 px-3 rounded-md glass border border-white/10 bg-transparent text-foreground"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat} className="bg-background-paper">
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="venue">Venue *</Label>
              <Input
                id="venue"
                value={formData.venue}
                onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                placeholder="Madison Square Garden, New York"
                className="glass border-white/10"
              />
            </div>

            <div className="flex items-center justify-between pt-4">
              <div className="space-y-1">
                <Label>Virtual Event</Label>
                <p className="text-sm text-gray-400">Enable online streaming</p>
              </div>
              <Switch
                checked={formData.is_virtual}
                onCheckedChange={(checked) => setFormData({ ...formData, is_virtual: checked })}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Date & Time */}
      {currentStep === 2 && (
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle>Date & Time</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Start Date & Time *</Label>
                <Input
                  id="date"
                  type="datetime-local"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="glass border-white/10"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_date">End Date & Time (Optional)</Label>
                <Input
                  id="end_date"
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="glass border-white/10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="capacity">Total Capacity</Label>
              <Input
                id="capacity"
                type="number"
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                className="glass border-white/10"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Ticket Tiers */}
      {currentStep === 3 && (
        <Card className="glass border-white/10">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Ticket Tiers</CardTitle>
            <Button onClick={addTicketTier} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Tier
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Currency Selection */}
            <div className="space-y-2 pb-4 border-b border-white/10">
              <Label>Currency</Label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full md:w-48 h-10 px-3 rounded-md glass border border-white/10 bg-transparent text-foreground"
              >
                {currencies.map((curr) => (
                  <option key={curr.code} value={curr.code} className="bg-background-paper">
                    {curr.symbol} {curr.code} - {curr.name}
                  </option>
                ))}
              </select>
            </div>
            
            {ticketTiers.map((tier, index) => (
              <Card key={index} className="glass border-white/10">
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge>Tier {index + 1}</Badge>
                    {ticketTiers.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTicketTier(index)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tier Name</Label>
                      <Input
                        value={tier.name}
                        onChange={(e) => updateTicketTier(index, 'name', e.target.value)}
                        placeholder="VIP"
                        className="glass border-white/10"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Price ({currencies.find(c => c.code === formData.currency)?.symbol || '$'})</Label>
                      <Input
                        type="number"
                        value={tier.price}
                        onChange={(e) => updateTicketTier(index, 'price', parseFloat(e.target.value))}
                        className="glass border-white/10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={tier.description}
                      onChange={(e) => updateTicketTier(index, 'description', e.target.value)}
                      placeholder="Standard entry with access to all areas"
                      className="glass border-white/10"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Capacity</Label>
                    <Input
                      type="number"
                      value={tier.capacity}
                      onChange={(e) => updateTicketTier(index, 'capacity', parseInt(e.target.value))}
                      className="glass border-white/10"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Step 4: Additional Details */}
      {currentStep === 4 && (
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle>Additional Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Event Poster Upload */}
            <div className="space-y-3">
              <Label>Event Poster</Label>
              <div 
                className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                  posterPreview ? 'border-primary' : 'border-white/20 hover:border-white/40'
                }`}
              >
                {posterPreview ? (
                  <div className="relative">
                    <img 
                      src={posterPreview} 
                      alt="Event poster preview" 
                      className="max-h-64 mx-auto rounded-lg object-cover"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="absolute top-2 right-2 glass"
                      onClick={() => {
                        setPosterPreview(null);
                        setFormData({ ...formData, poster_url: '' });
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div 
                    className="cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="flex flex-col items-center space-y-3">
                      {isUploading ? (
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                      ) : (
                        <div className="p-4 rounded-full bg-primary/10">
                          <ImageIcon className="h-8 w-8 text-primary" />
                        </div>
                      )}
                      <div>
                        <p className="text-foreground font-medium">
                          {isUploading ? 'Uploading...' : 'Click to upload poster image'}
                        </p>
                        <p className="text-sm text-gray-400 mt-1">
                          PNG, JPG, WebP or GIF (max 10MB)
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePosterUpload}
                  className="hidden"
                />
              </div>
              
              {/* Or enter URL manually */}
              <div className="text-center text-sm text-gray-500 my-2">or</div>
              <Input
                id="poster_url"
                value={formData.poster_url}
                onChange={(e) => {
                  setFormData({ ...formData, poster_url: e.target.value });
                  if (e.target.value && !posterPreview) {
                    setPosterPreview(e.target.value);
                  }
                }}
                placeholder="Enter poster image URL"
                className="glass border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="music, edm, dance, party"
                className="glass border-white/10"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <Button
          onClick={handleBack}
          variant="outline"
          disabled={currentStep === 1}
          className="glass-hover"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {currentStep < 4 ? (
          <Button onClick={handleNext} className="shadow-glow">
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <div className="flex space-x-2">
            <Button
              onClick={() => handleSave('draft')}
              variant="outline"
              disabled={isSaving}
              className="glass-hover"
            >
              <Save className="h-4 w-4 mr-2" />
              Save as Draft
            </Button>
            <Button
              onClick={() => handleSave('published')}
              disabled={isSaving}
              className="shadow-glow"
            >
              Publish Event
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
